import React, { useState, useEffect, useMemo, useRef } from "react";
import {
    Typography,
    FormControl,
    Select,
    MenuItem,
    Grid,
    Box,
    Paper,
    Button,
    makeStyles,
    Snackbar,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Switch,
    Link,
    withStyles
} from "@material-ui/core";
import { Check } from "@material-ui/icons";
import axios from "axios";
import { appUrl } from "../../../../../../../utils/config";
import MuiAlert from "@material-ui/lab/Alert";
import ProcessFlowMappingDialog from './../../ModalPopups/ProcessFlowMappingDialog';
import ProcessFlowVersionHistoryDialog from './../../ModalPopups/ProcessFlowVersionHistoryDialog';
import { useApiBaseUrl } from './../../../../common/CommonMethods/CommonMethods';

const Alert = (props) => <MuiAlert elevation={6} variant="filled" {...props} />;

const useStyles = makeStyles((theme) => ({
    paper: { padding: 0 },
    formControl: { width: "100%" },
    label: { marginBottom: theme.spacing(1), fontWeight: 900 },
    select: { backgroundColor: "#fff", width: "100%" },
    button: { 
        marginTop: theme.spacing(1), 
        marginBottom: theme.spacing(1),
        minWidth: 180,
        // fontWeight: 600,
        // fontSize: 16,
        borderRadius: 30,
        // height: 44,
    },
    requiredField: { color: "red" },
    iosSwitch: {
        '& > *': {
            height: '22px !important',
        },
        '& .MuiSwitch-switchBase .MuiSwitch-track': {
            backgroundColor: '#c6c6c6 !important',
        },
        '& .MuiSwitch-switchBase': {
            left: '2px',
            top: '-1px',
        },
        '& .MuiSwitch-switchBase.Mui-checked': {
            left: 'auto',
            right: '18px',
        },
        '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-track': {
            backgroundColor: '#1890ff !important',
        },
        '& .MuiSwitch-switchBase.Mui-disabled .MuiSwitch-track': {
            backgroundColor: '#e3f2fd !important',
        },
    },
    checkedSwitch: {
        position: 'absolute',
        width: '16px !important',
        left: '12px',
        top: '-3px',
        color: 'white',
    },
}));

// Add IOSSwitch from reference
const IOSSwitch = withStyles((theme) => ({
    root: {
        width: 44,
        height: 22,
        padding: 0,
        margin: theme.spacing(1),
    },
    switchBase: {
    padding: 1,
    "& .MuiSwitch-track": {
      backgroundColor: "#c6c6c6 !important",
    },
    "&$checked": {
      transform: "translateX(16px)",
      color: theme.palette.common.white,
      "& + $track": {
        backgroundColor: "#1890ff !important",
        opacity: 1,
        border: "none",
      },
      "& .MuiSwitch-track": {
        backgroundColor: "#1890ff !important",
      },
      "&$checked": {
      "& + $track": {
        backgroundColor: "#8BC6FD !important",
      },
      "& .MuiSwitch-track": {
        backgroundColor: "#8BC6FD !important",
      },
    },
    },
    "&$focusVisible $thumb": {
      color: "#52d869",
      border: "6px solid #fff",
    },
  },
    thumb: {
        width: 18,
        height: 18,
    },
    track: {
        borderRadius: 26 / 2,
        backgroundColor: "#c6c6c6",
        opacity: 1,
        transition: theme.transitions.create(["background-color", "border"]),
    },
    checked: {},
    focusVisible: {},
}))(props => <Switch focusVisibleClassName={props.classes.focusVisible} disableRipple {...props} />);

const getBrandOwnerMosymphonyID = () => {
    const token = localStorage.getItem('authToken');
    if (!token) return null;
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map(c => `%${('00' + c.charCodeAt(0).toString(16)).slice(-2)}`)
                .join('')
        );
        const payload = JSON.parse(jsonPayload);
        return payload.organization_id;
    } catch {
        return null;
    }
};

const ProcessFlowSelectionTable = React.forwardRef(({ readOnly }, ref) => {
    const [processFlows, setProcessFlows] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error', key: Date.now() });
    const classes = useStyles();
    const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
    const [selectedProcessFlow, setSelectedProcessFlow] = useState(null);
    const [versionHistoryDialogOpen, setVersionHistoryDialogOpen] = useState(false);
    const [selectedVersionHistoryFlow, setSelectedVersionHistoryFlow] = useState(null);

    const API_BASE_URL = useApiBaseUrl(appUrl)

    useEffect(() => {
        const fetchProcessFlows = async () => {
            if (!API_BASE_URL) return;

            setIsLoading(true);
            try {
                const res = await axios.get(`${API_BASE_URL}/fetchAllProcessFlows/true`);
                if (res.data && res.data.status && res.data.processFlowsList) {
                    setProcessFlows(res.data.processFlowsList);
                } else {
                    setSnackbar({
                        open: true,
                        message: res.data?.message || 'Failed to fetch process flows',
                        severity: 'error',
                        key: Date.now()
                    });
                }
            } catch (e) {
                console.error('Error fetching process flows:', e);
                setSnackbar({
                    open: true,
                    message: 'Failed to fetch process flows. Please try again.',
                    severity: 'error',
                    key: Date.now()
                });
            } finally {
                setIsLoading(false);
            }
        };
        fetchProcessFlows();
    }, [API_BASE_URL]);

    const handleToggle = (id) => {
        setProcessFlows((prev) => prev.map(flow =>
            flow.processFlowID === id ? { ...flow, activated: !flow.activated } : flow
        ));
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === "clickaway") return;
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    // Expose process flows and save function to parent component
    React.useImperativeHandle(ref, () => ({
        getProcessFlows: () => processFlows,
        saveProcessFlows: async () => {
            if (!API_BASE_URL) return { success: false, message: 'API base URL not available' };

            setSaving(true);    
            try {
                const payload = {
                    sponsorId: 2, // Example sponsorId
                    ecosystemId: localStorage.getItem("ecosystemId"), // Example ecosystemId
                    processFlowsList: processFlows.map(flow => ({
                        ...flow,
                        activated: !!flow.activated,
                        markForDelete: false,
                        processFlowType: flow.processFlowType || 'Default',
                    }))
                };
                const response = await axios.post(`${API_BASE_URL}/addProcessFlows`, payload);

                if (response.data && response.data.status) {
                    return { success: true, message: 'Process Flows saved successfully!' };
                } else {
                    return { success: false, message: response.data?.message || 'Failed to save process flows' };
                }
            } catch (e) {
                console.error('Error saving process flows:', e);
                return { success: false, message: 'Failed to save process flows. Please try again.' };
            } finally {
                setSaving(false);
            }
        }
    }), [processFlows, API_BASE_URL]);

    return (
        <Box mt={6}>
            <Typography variant="h6">Process Flow Selection</Typography>
            <TableContainer component={Paper} style={{ marginTop: 24 }}>
                <Table>
                    <TableHead>
                        <TableRow style={{ background: '#F4F6F8' }}>
                            {[
                                'Selection',
                                'Process Flow',
                                'Process Flow ID',
                                'Process Flow Mapping',
                                'Version History',
                                'QA Check Req.',
                                'Vendor - Destination Location',
                            ].map((header) => (
                                <TableCell
                                    key={header}
                                    style={{
                                        fontWeight: 700,
                                        background: '#F4F6F8',
                                        borderBottom: '2px solid #E0E0E0',
                                        color: '#222',
                                        fontSize: 16,
                                        padding: '12px 16px',
                                    }}
                                >
                                    {header}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    <CircularProgress size={24} />
                                </TableCell>
                            </TableRow>
                        ) : processFlows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    <Typography>No process flows found</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            processFlows.map((flow, index) => (
                                <TableRow key={`${flow.processFlowID}-${index}`}>
                                    <TableCell>
                                        <span style={{ position: "relative" }}>
                                            <IOSSwitch
                                                className={classes.iosSwitch}
                                                color="primary"
                                                checked={!!flow.activated}
                                                onChange={e => handleToggle(flow.processFlowID)}
                                                disabled={readOnly || true }
                                            />
                                            {!!flow.activated && (
                                                <Check className={classes.checkedSwitch} />
                                            )}
                                        </span>
                                    </TableCell>
                                    <TableCell >{flow.processFlowName}</TableCell>
                                    <TableCell align="center">{flow.processFlowID}</TableCell>
                                    <TableCell>
                                        <Link
                                            style={{ color: '#1890ff', cursor: 'pointer' }}
                                            onClick={() => {
                                                setSelectedProcessFlow(flow);
                                                setMappingDialogOpen(true);
                                            }}
                                        >
                                            Click here view
                                        </Link>
                                    </TableCell>
                                    <TableCell align="center">
                                        {flow.versionHistory && flow.versionHistory.length > 0 ? (
                                            <Link
                                                style={{ color: '#1890ff', cursor: 'pointer' }}
                                                onClick={() => {
                                                    setSelectedVersionHistoryFlow(flow);
                                                    setVersionHistoryDialogOpen(true);
                                                }}
                                            >
                                                Click here view
                                            </Link>
                                        ) : (
                                            ''
                                        )}
                                    </TableCell>
                                    <TableCell align="center">{flow.qaCheckRequired ? 'Yes' : 'No'}</TableCell>
                                    <TableCell>
                                        {flow.cmoCountry && flow.addresseeOrgName && flow.addresseeCountry
                                            ? `${flow.cmoCountry} - ${flow.addresseeOrgName} - ${flow.addresseeCountry}`
                                            : 'NA'}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <ProcessFlowMappingDialog
                open={false}
                onClose={() => setMappingDialogOpen(false)}
                processFlow={selectedProcessFlow}
            />
            <ProcessFlowVersionHistoryDialog
                open={false}
                onClose={() => setVersionHistoryDialogOpen(false)}
                processFlow={selectedVersionHistoryFlow}
            />

            {/* Snackbar for notifications */}
            <Snackbar
                key={snackbar.key}
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                style={{ zIndex: 9999 }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    style={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
});

const ProcessFlowConfiguration = ({
    formData,
    updateStepData,
    classes: parentClasses,
    readOnly = false,
    handleNext,
    handleBack,
    handleSubmit,
    activeStep,
    stepsLength,
    isProcessFlowConfigEditMode = false,
    onValidationChange = () => { },
    viewOnlyProductDetails = false,
    markApiCallSuccessful, // Function to mark API call as successful
}) => {
    const classes = useStyles();
    const API_BASE_URL = useApiBaseUrl(appUrl);
    const processFlowTableRef = useRef();

    // Only two fields as per new requirements
    const [stepData, setStepData] = useState(() => {
        const base = formData?.processFlowConfiguration || {};
        return {
            sponsorCQAApprovalReq: base?.sponsorCQAApprovalReq ?? false,
            stockCheckReq: base?.stockCheckReq ?? false,
        };
    });

    useEffect(() => {
        if (formData && formData?.processFlowConfiguration) {
            setStepData({
                sponsorCQAApprovalReq: formData.processFlowConfiguration.sponsorCQAApprovalReq ?? false,
                stockCheckReq: formData.processFlowConfiguration.stockCheckReq ?? false,
            });
        }
    }, [formData]);

    // No validation required as both are boolean dropdowns
    useEffect(() => {
        onValidationChange(true);
    }, [stepData, onValidationChange]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        // Prevent changes to sponsorCQAApprovalReq as it's read-only
        // if (name === 'sponsorCQAApprovalReq') {
        //     return;
        // }
        
        // Handle null and string/boolean conversion for dropdowns
        let safeValue;
        if (value === null || value === "null") {
            safeValue = false; // Default to false if null
        } else if (value === true || value === "true") {
            safeValue = true;
        } else if (value === false || value === "false") {
            safeValue = false;
        } else {
            safeValue = Boolean(value);
        }
        const updatedStepData = { ...stepData, [name]: safeValue };
        setStepData(updatedStepData);
        if (typeof updateStepData === "function") {
            updateStepData("processFlowConfiguration", updatedStepData);
        }
    };

    const [isLoading, setIsLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "error",
        key: Date.now(),
    });

    const handleCloseSnackbar = (event, reason) => {
        if (reason === "clickaway") return;
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    const saveProcessFlowConfiguration = async () => {
        try {
            setIsLoading(true);

            // // First save the process flow configurations
            // if (processFlowTableRef.current) {
            //     const processFlowResult = await processFlowTableRef.current.saveProcessFlows();
            //     if (!processFlowResult.success) {
            //         setSnackbar({
            //             open: true,
            //             message: processFlowResult.message,
            //             severity: "error",
            //         });
            //         return;
            //     }
            // }

            // const productConfigFormData = sessionStorage.getItem("productConfigFormData");
            // const parsedFormData = productConfigFormData ? JSON.parse(productConfigFormData) : {};
            const payload = {
                updateProcessFlowConfigFlag: true,
                productDetails: {
                    vendorMosymphonyID: formData?.productDetails?.vendorMosymphonyID,
                    collabrixProductID: formData?.productDetails?.collabrixProductID,
                },
                processFlowConfiguration: {
                    // sponsorCQAApprovalReq moved to productDetails (per BCM-393) - this
                    // step only owns stockCheckReq now.
                    stockCheckReq: stepData.stockCheckReq,
                }
            };

            const response = await axios.post(
                `${API_BASE_URL}/updateProductDetails`,
                payload
            );

            // Mark API call as successful
            if (typeof markApiCallSuccessful === 'function' && response.data && response.data.status) {
                markApiCallSuccessful();
                return;
            }

            if (response.data && response.data.status) {
                // This section will run only if markApiCallSuccessful is not available
                handleNext();
                setSnackbar({
                    open: true,
                    message: "Process Flow configurations saved successfully!",
                    severity: "success",
                });
            } else {
                setSnackbar({
                    open: true,
                    message: "Failed to save other configurations. Please try again.",
                    severity: "error",
                });
            }
        } catch (error) {
            setSnackbar({
                open: true,
                message: "An error occurred while saving.",
                severity: "error",
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <form>
                <Paper elevation={0} className={classes.paper}>
                    <Typography variant="h6">Process flow configurations</Typography>
                    <Grid container spacing={3} className="two-col-forms-grid">
                        {/* BCM-393: "Sponsor QA check required" moved to the Product Details
                            step (as "QA Check Required") and now lives under productDetails
                            on the wire - removed from this step's UI and payload. */}
                        <Grid item xs={12} md={6}>
                            <FormControl className={classes.formControl}>
                                <Typography component="label" className={classes.label}>
                                    Stock check required
                                    {!readOnly && <span className={classes.requiredField}> *</span>}
                                </Typography>
                                {!readOnly ? (
                                    <Select
                                        value={stepData?.stockCheckReq}
                                        onChange={handleChange}
                                        name="stockCheckReq"
                                        variant="outlined"
                                        className={classes.select}
                                    >
                                        <MenuItem value={true}>YES</MenuItem>
                                        <MenuItem value={false}>NO</MenuItem>
                                    </Select>
                                ) : (
                                    <Typography>
                                        {stepData?.stockCheckReq ? "YES" : "NO"}
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>
                    </Grid>
                    <ProcessFlowSelectionTable ref={processFlowTableRef} readOnly={readOnly} />
                </Paper>

                {(!readOnly && !viewOnlyProductDetails) && (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: activeStep === 0 ? "center" : "space-between",
                        }}
                        className="stepper-navigation-btns"
                        mt={4}
                    >
                        {activeStep !== 0 && (
                            <>
                                <Button
                                    variant="outlined"
                                    disabled={activeStep === 0}
                                    onClick={handleBack}
                                    className={classes.button}
                                >
                                    Back
                                </Button>
                                <Box sx={{ flex: "1 1 auto" }} />
                            </>
                        )}
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={activeStep === stepsLength - 1 ? handleSubmit : saveProcessFlowConfiguration}
                            className={classes.button}
                            disabled={isLoading}
                        >
                            {isLoading ? <CircularProgress size={20} color="inherit" /> : activeStep === stepsLength - 1 ? "Save" : "Save & Next"}
                        </Button>
                    </Box>
                )}

                {/* Snackbar */}
                <Snackbar
                    key={snackbar.key}
                    open={snackbar.open}
                    autoHideDuration={3000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: "top", horizontal: "center" }}
                    style={{ zIndex: 9999 }}
                >
                    <Alert
                        onClose={handleCloseSnackbar}
                        severity={snackbar.severity}
                        style={{ width: "100%" }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </form>
        </>
    );
};

export default ProcessFlowConfiguration;